import { Link, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { motion, useInView } from "framer-motion";
import { useEffect, useState, useRef } from "react";

// ─── Reusable animation variants ───────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 },
  }),
};

const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.1 },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

// ─── Hook: trigger animation once when element enters viewport ─────────────
function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  return [ref, isInView];
}

function Category() {
  const createSlug = (text) =>
    text
      ?.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");

  const [courses, setCourses] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { categoryName } = useParams();

  useEffect(() => {
    const url = categoryName
      ? `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?courseCategory=${encodeURIComponent(categoryName)}&isActive=true`
      : `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?limit=100&isActive=true`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        const all = data.data || [];
        setCourses(all.filter((c) => c.status === "published"));
      })
      .catch((err) => console.error("Courses API error:", err));
  }, [categoryName]);

  const limitWords = (text, limit) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > limit
      ? words.slice(0, limit).join(" ") + "..."
      : text;
  };

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?limit=8&category",
        );
        if (!res.ok) throw new Error("Failed to fetch course");
        const data = await res.json();
        setCategory(data?.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, []);

  // ── Section refs ──────────────────────────────────────────────────────────
  const [categoryRef, categoryInView] = useScrollReveal(0.1);
  const [headingRef, headingInView] = useScrollReveal(0.15);
  const [coursesRef, coursesInView] = useScrollReveal(0.1);
  const [ctaRef, ctaInView] = useScrollReveal(0.2);

  return (
    <>
      <Header />

      {/* ── Category Pills ─────────────────────────────────────────────── */}
      <section className="pt-2 pb-3" ref={categoryRef}>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={categoryInView ? "visible" : "hidden"}
        >
          <div className="container">
            <div className="row pt-4">
              <div className="col-12">
                <motion.div
                  className="row g-2"
                  variants={staggerContainer}
                  initial="hidden"
                  animate={categoryInView ? "visible" : "hidden"}
                >
                  {category?.map((item, index) => (
                    <div className="col-3" key={index}>
                      <motion.div
                        className="card card-bordered shadow rounded-4 card-hover cursor-pointer h-100"
                        variants={fadeUp}
                        custom={index}
                        whileHover={{
                          y: -4,
                          scale: 1.02,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                        }}
                        transition={{ type: "spring", stiffness: 250 }}
                      >
                        <div className="card-body d-flex align-items-center py-3">
                          <div>
                            <div className="d-md-flex">
                              <div>
                                <div className="icon-shape icon-md bg-light-primary rounded-circle">
                                  <i className="bi bi-card-text fs-4 text-primary"></i>
                                </div>
                              </div>
                              <div className="ms-md-3 w-100">
                                <div className="d-flex justify-content-between">
                                  <div>
                                    <h3 className="fs-4 m-0 lh-1">
                                      <Link
                                        to={`/category/${item?.category}`}
                                        className="text-dark"
                                      >
                                        {item?.category}
                                      </Link>
                                    </h3>
                                    <div>
                                      <span>{item?.count} Courses</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Courses Section ────────────────────────────────────────────── */}
      <section className="bg-white">
        <div className="container">
          {/* Heading */}
          <motion.div
            className="row pt-5"
            ref={headingRef}
            variants={fadeUp}
            initial="hidden"
            animate={headingInView ? "visible" : "hidden"}
          >
            <div className="col-12">
              <span className="badge bg-dark-soft rounded-pill fs-5 px-4">
                Our <span className="fw-bold">Courses</span>
              </span>
              <motion.h1
                className="mb-0 display-6 mt-2"
                initial={{ opacity: 0, y: 20 }}
                animate={headingInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                {categoryName} Courses{" "}
                <u className="text-primary">
                  <span className="text-dark">for everyone</span>
                </u>
              </motion.h1>
              <motion.p
                className="lead lh-sm"
                initial={{ opacity: 0, y: 15 }}
                animate={headingInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.25 }}
              >
                Choose from a wide range of stock market courses on trading and
                investing.
              </motion.p>
            </div>
          </motion.div>

          {/* Course cards */}
          <motion.div
            className="row g-2 pb-7"
            ref={coursesRef}
            variants={staggerContainer}
            initial="hidden"
            animate={coursesInView ? "visible" : "hidden"}
          >
            {courses.slice(0, 6).map((course, index) => (
              <div className="col-lg-4 col-12" key={index}>
                <div className="row g-0 rounded-5 h-100">
                  <motion.div
                    className="col-12 px-4 d-flex align-items-center bg-light pb-4 pt-1 rounded-4"
                    variants={fadeUp}
                    custom={index}
                    whileHover={{
                      y: -6,
                      boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                    }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <div className="d-flex flex-column px-2 mb-2 w-100">
                      <div className="d-flex flex-column gap-3 mt-4">
                        <div className="d-flex flex-column">
                          <h3 className="mb-0 lh-1">
                            <Link
                              to={`/course/${course?.courseId}/${createSlug(course?.title)}`}
                              className="fs-3 text-dark"
                            >
                              {course?.title}
                            </Link>
                          </h3>
                          <p className="mb-0 lh-1 mt-1 mb-2">
                            {course?.about?.split(" ").slice(0, 15).join(" ")}
                            ...
                          </p>
                          <span className="text-gray-500">
                            By <a href="#">{course?.instructors[0]?.name}</a>
                          </span>
                        </div>
                        <div className="lh-1">
                          <span className="text-secondary fw-semibold">
                            4.5
                          </span>
                          <span className="align-text-top ms-3 me-2">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className="bi bi-star-fill text-warning"
                              ></i>
                            ))}
                          </span>
                          <span className="text-gray-500">(13,245)</span>
                        </div>
                      </div>
                      <div className="d-flex flex-row gap-2 align-items-center">
                        <span className="fs-3 lh-1 mt-2 fw-semibold text-dark">
                          ₹{course?.offeredPrice} + GST
                        </span>
                      </div>
                      <span className="fw-medium lh-sm text-gray-400">
                        <del>
                          ₹{course?.originalPrice?.toLocaleString("en-IN")}
                        </del>
                      </span>
                      <div className="mt-3">
                        <motion.div
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.96 }}
                          style={{ display: "inline-block" }}
                        >
                          <Link
                            to={`/course/${course?.courseId}/${createSlug(course?.title)}`}
                            className="btn bg-gold text-dark w-auto px-5 rounded-pill border-0"
                          >
                            View Course
                          </Link>
                        </motion.div>
                        <Link className="btn btn-icon btn-success rounded-circle d-none d-md-inline-flex ms-1">
                          <i className="bi bi-whatsapp fs-4 align-middle"></i>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            ))}

            {/* Empty state */}
            {!loading && courses.length === 0 && (
              <motion.div
                className="col-12 text-center py-7"
                variants={fadeUp}
                initial="hidden"
                animate={coursesInView ? "visible" : "hidden"}
              >
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <i
                    className="bi bi-search"
                    style={{ fontSize: 32, color: "#aaa" }}
                  ></i>
                </div>
                <h4 className="text-dark fw-semibold mb-2">No Courses Found</h4>
                <p className="text-muted fs-5 mb-4">
                  No courses available in the "{categoryName}" category yet.
                </p>
                <Link to="/" className="btn btn-primary px-6 rounded-pill">
                  Browse All Courses
                </Link>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── CTA Banner ─────────────────────────────────────────────────── */}
      <section ref={ctaRef}>
        <div className="container mt-3">
          <motion.div
            className="bg-info p-6 rounded-top-5"
            variants={scaleIn}
            initial="hidden"
            animate={ctaInView ? "visible" : "hidden"}
          >
            <div className="container">
              <div className="row">
                <motion.div
                  className="col-lg-8"
                  variants={slideLeft}
                  initial="hidden"
                  animate={ctaInView ? "visible" : "hidden"}
                >
                  <h5 className="mb-0 fs-3 fw-normal lh-sm text-light">
                    Free 7-Day Masterclass — Intraday & Option Chain Data
                    Trading
                  </h5>
                  <h2 className="h1 text-white fw-normal ls-sm lh-1">
                    Trusted By <span className="fw-bold">10K+</span> Users
                  </h2>
                  <div className="mt-n4 pt-2">
                    <img
                      src="/assets/images/line-shap.webp"
                      width="230"
                      className="mt-2"
                    />
                  </div>
                </motion.div>
                <motion.div
                  className="col-lg-4 d-flex justify-content-lg-end justify-content-center align-items-center"
                  variants={slideRight}
                  initial="hidden"
                  animate={ctaInView ? "visible" : "hidden"}
                >
                  <motion.a
                    href="https://www.youtube.com/@SLNAcademyOfficial"
                    target="_blank"
                    className="btn btn-light btn-md px-7 rounded-4 mb-lg-0 mb-5"
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Subscribe Now
                  </motion.a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Category;
